import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MenuProductService } from '../../../services/menu-product/menu-product.service';
import { DatePickerComponent } from '../../atoms/date-picker/date-picker.component';
import { InputComponent } from '../../atoms/input/input.component';

export interface PriceHistoryDialogData {
  productUuid: string;
  productName: string;
}

@Component({
  selector: 'app-price-history-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatIconModule,
    DatePickerComponent,
    InputComponent,
  ],
  template: `
    <h2 mat-dialog-title class="dialog-title">
      <mat-icon class="title-icon">history</mat-icon>
      Historial de precios
      <span class="product-name">{{ data.productName }}</span>
    </h2>

    <div mat-dialog-content class="dialog-body">
      <!-- New entry form -->
      <div class="new-entry-card">
        <p class="section-label">Registrar precio histórico</p>

        <div class="date-row">
          <span class="field-label">Fecha</span>
          <ui-date-picker [(ngModel)]="histDate" [max]="today" />
        </div>

        <div class="price-row">
          <ui-input
            label="$ Venta"
            type="number"
            [value]="histSellPrice ?? ''"
            (valueChange)="histSellPrice = +$event || null"
            placeholder="0"
            [min]="0"
          />
          <ui-input
            label="$ Costo"
            type="number"
            [value]="histCostPrice ?? ''"
            (valueChange)="histCostPrice = +$event || null"
            placeholder="0"
            [min]="0"
          />
        </div>

        <button mat-flat-button color="primary" class="save-btn"
          [disabled]="histSaving || histSellPrice === null || histCostPrice === null || !histDate"
          (click)="save()">
          <mat-icon>add_circle</mat-icon>
          {{ histSaving ? 'Guardando…' : 'Registrar' }}
        </button>
      </div>

      <!-- History list -->
      <div class="history-section">
        <p class="section-label">Registros anteriores</p>

        <div *ngIf="loading" class="empty-msg">
          <mat-icon class="spin-icon">hourglass_empty</mat-icon> Cargando…
        </div>
        <div *ngIf="!loading && history.length === 0" class="empty-msg">
          Sin registros de precio
        </div>

        <div *ngIf="!loading && history.length > 0" class="hist-table">
          <div class="hist-row hist-header">
            <span>Fecha</span>
            <span>Venta</span>
            <span>Costo</span>
            <span>Por</span>
          </div>
          <div *ngFor="let h of history" class="hist-row">
            <span class="text-muted">{{ h.changedAt | date:'dd/MM/yy HH:mm' }}</span>
            <span class="fw-bold">\${{ h.sellPrice | number:'1.0-0' }}</span>
            <span class="text-muted">\${{ h.costPrice | number:'1.0-0' }}</span>
            <span class="text-xs text-muted">{{ h.changedByName || '—' }}</span>
          </div>
        </div>
      </div>
    </div>

    <div mat-dialog-actions align="end">
      <button mat-stroked-button (click)="dialogRef.close()">Cerrar</button>
    </div>
  `,
  styles: [`
    .dialog-title {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 16px;
      flex-wrap: wrap;
    }
    .title-icon { color: #f26600; font-size: 20px; width: 20px; height: 20px; }
    .product-name {
      font-size: 12px;
      font-weight: 400;
      color: #6b7280;
      margin-left: 4px;
    }

    .dialog-body { padding: 0 4px; display: flex; flex-direction: column; gap: 20px; min-width: 300px; }

    .section-label {
      font-size: 11px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #9ca3af;
      margin: 0 0 10px;
    }

    .new-entry-card {
      background: #fafafa;
      border: 1px solid #e5e7eb;
      border-radius: 12px;
      padding: 14px;
    }

    .date-row {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 12px;
    }
    .field-label { font-size: 13px; color: #374151; white-space: nowrap; min-width: 42px; }
    .date-row ui-date-picker { flex: 1; }

    .price-row {
      display: flex;
      gap: 10px;
      margin-bottom: 12px;
    }
    .price-row ui-input { flex: 1; }

    .save-btn { width: 100%; }

    .history-section { padding-bottom: 4px; }

    .empty-msg {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 13px;
      color: #9ca3af;
      padding: 10px 0;
      justify-content: center;
    }

    .hist-table {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      overflow: hidden;
      font-size: 12px;
    }

    .hist-row {
      display: grid;
      grid-template-columns: 2fr 1fr 1fr 1.5fr;
      gap: 6px;
      padding: 6px 10px;
      align-items: center;
    }
    .hist-row + .hist-row { border-top: 1px solid #f3f4f6; }

    .hist-header {
      background: #f1f5f9;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #6b7280;
    }

    .fw-bold { font-weight: 600; }
    .text-muted { color: #6b7280; }
    .text-xs { font-size: 11px; }

    .spin-icon { animation: spin 1s linear infinite; font-size: 16px; width: 16px; height: 16px; }
    @keyframes spin { to { transform: rotate(360deg); } }
  `]
})
export class PriceHistoryDialogComponent implements OnInit {
  today = new Date().toISOString().substring(0, 10);
  histDate = this.today;
  histSellPrice: number | null = null;
  histCostPrice: number | null = null;
  histSaving = false;

  history: any[] = [];
  loading = false;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: PriceHistoryDialogData,
    public dialogRef: MatDialogRef<PriceHistoryDialogComponent>,
    private menuProductService: MenuProductService,
  ) {}

  ngOnInit(): void {
    this.loadHistory();
  }

  loadHistory(): void {
    this.loading = true;
    this.menuProductService.getPriceHistory(this.data.productUuid).subscribe({
      next: (resp: any) => {
        const rows = Array.isArray(resp) ? resp : (Array.isArray(resp?.data) ? resp.data : []);
        this.history = rows;
        this.loading = false;
      },
      error: () => { this.loading = false; }
    });
  }

  save(): void {
    if (this.histSellPrice === null || this.histCostPrice === null || !this.histDate) return;
    this.histSaving = true;
    this.menuProductService.addPriceHistory(this.data.productUuid, {
      sellPrice: this.histSellPrice,
      costPrice: this.histCostPrice,
      date: this.histDate,
    }).subscribe({
      next: () => {
        this.histSaving = false;
        this.histSellPrice = null;
        this.histCostPrice = null;
        this.histDate = this.today;
        this.loadHistory();
      },
      error: () => { this.histSaving = false; }
    });
  }
}
